'use client'

import { useState, useMemo, useEffect } from 'react'
import { formatPrice, formatChange } from '@/lib/formatters'
import type { QuarterlyPrediction } from '@/lib/technicals'

interface Props {
  quarterlyPredictions: QuarterlyPrediction[]
  coinName: string
  symbol: string
  currentPrice: number
}

export function YearlyForecast({ quarterlyPredictions, coinName, symbol, currentPrice }: Props) {
  const years = useMemo(
    () => [...new Set(quarterlyPredictions.map((p) => p.year))].sort(),
    [quarterlyPredictions]
  )
  const [activeYear, setActiveYear] = useState(years[0])
  const [expandedQ, setExpandedQ] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const quarters = useMemo(
    () => quarterlyPredictions.filter((p) => p.year === activeYear),
    [quarterlyPredictions, activeYear]
  )

  // Year summary
  const yearLow = quarters.length > 0 ? Math.min(...quarters.map((q) => q.low)) : 0
  const yearHigh = quarters.length > 0 ? Math.max(...quarters.map((q) => q.high)) : 0
  const yearAvg = quarters.length > 0 ? quarters.reduce((a, q) => a + q.avg, 0) / quarters.length : 0
  const yearChange = ((yearAvg - currentPrice) / currentPrice) * 100

  // Scale
  const scaleLow = useMemo(
    () => Math.min(currentPrice * 0.95, ...quarters.map((q) => q.low)),
    [quarters, currentPrice]
  )
  const scaleHigh = useMemo(
    () => Math.max(currentPrice * 1.05, ...quarters.map((q) => q.high)),
    [quarters, currentPrice]
  )
  const scaleRange = scaleHigh - scaleLow || 1
  const nowPct = ((currentPrice - scaleLow) / scaleRange) * 100

  // Compact price
  const fmtC = (p: number) => {
    if (p >= 1e6) return `$${(p / 1e6).toFixed(1)}M`
    if (p >= 1e3) return `$${(p / 1e3).toFixed(1)}K`
    if (p >= 1) return `$${p.toFixed(0)}`
    if (p >= 0.01) return `$${p.toFixed(2)}`
    return `$${p.toPrecision(3)}`
  }

  return (
    <div>
      {/* ═══ YEAR PILLS ═══ */}
      <div className="flex items-center gap-0 border border-zinc-800 w-fit mb-4 max-w-full overflow-x-auto">
        {years.map((y) => (
          <button
            key={y}
            onClick={() => { setActiveYear(y); setExpandedQ(null) }}
            className={`px-4 py-2 text-xs font-medium transition-colors border-r border-zinc-800 last:border-r-0 whitespace-nowrap tabular-nums ${
              activeYear === y
                ? 'bg-zinc-800 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
            }`}
          >
            {y}
          </button>
        ))}
      </div>

      {/* ═══ QUARTERLY RANGE BARS ═══ */}
      <div className="border border-zinc-800 px-4 pt-2 pb-3">
        {quarters.map((q) => {
          const rawBarLeft = ((q.low - scaleLow) / scaleRange) * 100
          const rawBarWidth = ((q.high - q.low) / scaleRange) * 100
          const minWidth = isMobile ? 45 : 0.5
          const barWidth = Math.max(rawBarWidth, minWidth)
          const midpoint = rawBarLeft + rawBarWidth / 2
          const barLeft = isMobile
            ? Math.max(0, Math.min(midpoint - barWidth / 2, 100 - barWidth))
            : rawBarLeft
          const avgLeft = ((q.avg - scaleLow) / scaleRange) * 100
          const isExpanded = expandedQ === q.quarter
          const isPositive = q.change >= 0

          return (
            <div key={q.quarter}>
              <div
                className={`flex items-center gap-3 py-3 border-b border-zinc-800/40 cursor-pointer transition-colors hover:bg-zinc-900/30 ${
                  isExpanded ? 'bg-zinc-900/20' : ''
                }`}
                onClick={() => setExpandedQ(isExpanded ? null : q.quarter)}
              >
                {/* Quarter label */}
                <span
                  className={`w-7 text-sm flex-shrink-0 ${
                    isExpanded ? 'text-white font-semibold' : 'text-zinc-400 font-medium'
                  }`}
                >
                  {q.quarter}
                </span>

                {/* Bar area with price labels */}
                {(() => {
                  // Detect edge cases
                  const isNarrow = barWidth < 20
                  const nowOverlapsLow = Math.abs(nowPct - barLeft) < 10

                  return (
                    <div className="flex-1 relative h-6 sm:h-7">
                      {/* Now dashed line */}
                      <div
                        className="absolute top-[-4px] h-[32px] sm:h-[36px] border-l border-dashed border-zinc-600 pointer-events-none"
                        style={{ left: `${nowPct}%` }}
                      />
                      {/* Range bar */}
                      <div
                        className="absolute top-[14px] sm:top-[16px] h-[10px]"
                        style={{
                          left: `${barLeft}%`,
                          width: `${barWidth}%`,
                          background: isPositive ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)',
                          borderTop: `1px solid ${isPositive ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)'}`,
                          borderBottom: `1px solid ${isPositive ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)'}`,
                        }}
                      />
                      {/* Average tick */}
                      <div
                        className="absolute top-[8px] sm:top-[10px] w-[1px] sm:w-[2px] h-[22px] bg-white z-[1]"
                        style={{ left: `${avgLeft}%` }}
                      />

                      {isNarrow ? (
                        <>
                          {/* Narrow bar: labels spread outward */}
                          <span
                            className="absolute text-zinc-400 text-[10px] sm:text-[11px] tabular-nums whitespace-nowrap"
                            style={{
                              left: `${barLeft}%`,
                              top: 0,
                              transform: 'translateX(-100%) translateX(-4px)',
                            }}
                          >
                            {fmtC(q.low)}
                          </span>
                          <span
                            className="absolute text-zinc-400 text-[10px] sm:text-[11px] tabular-nums whitespace-nowrap"
                            style={{
                              left: `${barLeft + barWidth}%`,
                              top: 0,
                              transform: 'translateX(4px)',
                            }}
                          >
                            {fmtC(q.high)}
                          </span>
                        </>
                      ) : (
                        <>
                          {/* Normal bar: labels above edges */}
                          <span
                            className="absolute text-zinc-400 text-[10px] sm:text-[11px] tabular-nums whitespace-nowrap"
                            style={{
                              left: nowOverlapsLow ? `${barLeft + 8}%` : `${barLeft}%`,
                              top: 0,
                            }}
                          >
                            {fmtC(q.low)}
                          </span>
                          <span
                            className="absolute text-zinc-400 text-[10px] sm:text-[11px] tabular-nums whitespace-nowrap text-right"
                            style={{
                              left: `${barLeft + barWidth}%`,
                              top: 0,
                              transform: 'translateX(-100%)',
                            }}
                          >
                            {fmtC(q.high)}
                          </span>
                        </>
                      )}
                    </div>
                  )
                })()}

                {/* Change % */}
                <span
                  className={`w-14 sm:w-16 text-right text-xs font-semibold tabular-nums flex-shrink-0 ${
                    isPositive ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  {formatChange(q.change)}
                </span>

                {/* Expand +/− */}
                <span
                  className={`flex-shrink-0 text-base w-4 text-center leading-none select-none ${
                    isExpanded ? 'text-zinc-400' : 'text-zinc-600'
                  }`}
                >
                  {isExpanded ? '−' : '+'}
                </span>
              </div>

              {/* ═══ EXPANDED DETAIL ═══ */}
              {isExpanded && (
                <div className="bg-zinc-950/60 border-b border-zinc-800/40 px-4 py-3">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-zinc-400 text-[10px] uppercase tracking-wider mb-1 inline-flex items-center gap-1">
                        <svg width="6" height="6" viewBox="0 0 8 8"><path d="M4 7L1 3H7L4 7Z" fill="#ef4444" /></svg>
                        Min. price
                      </p>
                      <p className="text-zinc-300 text-sm tabular-nums">{formatPrice(q.low)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-400 text-[10px] uppercase tracking-wider mb-1 inline-flex items-center gap-1">
                        <svg width="8" height="6" viewBox="0 0 8 6"><path d="M0 3L3 0.5V5.5ZM8 3L5 0.5V5.5Z" fill="white" /></svg>
                        Avg. price
                      </p>
                      <p className="text-white text-sm tabular-nums font-medium">{formatPrice(q.avg)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-400 text-[10px] uppercase tracking-wider mb-1 inline-flex items-center gap-1">
                        <svg width="6" height="6" viewBox="0 0 8 8"><path d="M4 1L7 5H1L4 1Z" fill="#22c55e" /></svg>
                        Max. price
                      </p>
                      <p className="text-zinc-300 text-sm tabular-nums">{formatPrice(q.high)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {/* Now label */}
        <div className="relative h-4 mt-1 ml-[40px] mr-[80px]">
          <span
            className="absolute text-zinc-400 text-[9px] tabular-nums whitespace-nowrap"
            style={{ left: `${nowPct}%`, transform: 'translateX(-50%)' }}
          >
            Now {fmtC(currentPrice)}
          </span>
        </div>
      </div>

      {/* ═══ YEAR SUMMARY ═══ */}
      <p className="text-zinc-400 text-sm leading-relaxed mt-4 max-w-3xl">
        In {activeYear}, {coinName} ({symbol}) is anticipated to trade between{' '}
        <span className="text-white font-medium">{formatPrice(yearLow)}</span> and{' '}
        <span className="text-white font-medium">{formatPrice(yearHigh)}</span>, with an
        average price of{' '}
        <span className="text-white font-medium">{formatPrice(yearAvg)}</span>. This could
        result in a potential return of{' '}
        <span className={yearChange >= 0 ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}>
          {formatChange(yearChange)}
        </span>{' '}
        compared to the current price.
      </p>
    </div>
  )
}